import { createTheme, alpha, PaletteMode, Shadows } from '@mui/material/styles';

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}
declare module '@mui/material/styles/createPalette' {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange {}

  interface Palette {
    baseShadow: string;
  }
}

const defaultTheme = createTheme();
const customShadows: Shadows = [...defaultTheme.shadows];

export const brand = {
  50: '#F5F3FF',
  100: '#EDE9FE',
  200: '#DDD6FE',
  300: '#C4B5FD',
  400: '#A78BFA',
  500: '#8B5CF6',
  600: '#7C3AED',
  700: '#6D28D9',
  800: '#5B21B6',
  900: '#4C1D95',
};

export const indigo = {
  50: '#EEF2FF',
  100: '#E0E7FF',
  200: '#C7D2FE',
  300: '#A5B4FC',
  400: '#818CF8',
  500: '#6366F1',
  600: '#4F46E5',
  700: '#4338CA',
  800: '#3730A3',
  900: '#312E81',
};

export const gray = {
  50: '#FCFBFF',
  100: '#F5F3FF',
  200: '#E5E7EB',
  300: '#D7D4E8',
  400: '#AAA3C7',
  500: '#7C7698',
  600: '#5B5873',
  700: '#3D3951',
  800: '#221D33',
  900: '#14101F',
};

export const green = {
  50: '#F0FDF4',
  100: '#DCFCE7',
  200: '#BBF7D0',
  300: '#86EFAC',
  400: '#4ADE80',
  500: '#22C55E',
  600: '#16A34A',
  700: '#15803D',
  800: '#166534',
  900: '#14532D',
};

export const orange = {
  50: '#FFF7ED',
  100: '#FFEDD5',
  200: '#FED7AA',
  300: '#FDBA74',
  400: '#FB923C',
  500: '#F97316',
  600: '#EA580C',
  700: '#C2410C',
  800: '#9A3412',
  900: '#7C2D12',
};

export const red = {
  50: '#FEF2F2',
  100: '#FEE2E2',
  200: '#FECACA',
  300: '#FCA5A5',
  400: '#F87171',
  500: '#EF4444',
  600: '#DC2626',
  700: '#B91C1C',
  800: '#991B1B',
  900: '#7F1D1D',
};

const getCommonPalette = (mode: PaletteMode) => ({
  mode,
  primary: {
    light: brand[300],
    main: brand[600],
    dark: brand[800],
    contrastText: '#FFFFFF',
  },
  info: {
    light: indigo[300],
    main: indigo[500],
    dark: indigo[700],
    contrastText: '#FFFFFF',
  },
  warning: {
    light: orange[300],
    main: orange[500],
    dark: orange[800],
  },
  error: {
    light: red[300],
    main: red[500],
    dark: red[800],
  },
  success: {
    light: green[300],
    main: green[500],
    dark: green[800],
  },
  grey: {
    ...gray,
  },
  divider: mode === 'dark' ? alpha(brand[300], 0.16) : alpha(brand[700], 0.1),
  background: {
    default: mode === 'dark' ? gray[900] : gray[50],
    paper: mode === 'dark' ? '#1C1730' : '#FFFFFF',
  },
  text: {
    primary: mode === 'dark' ? '#FFFFFF' : gray[800],
    secondary: mode === 'dark' ? gray[300] : gray[600],
    warning: orange[500],
  },
  action: {
    hover: mode === 'dark' ? alpha(brand[300], 0.16) : alpha(brand[100], 0.6),
    selected: mode === 'dark' ? alpha(brand[400], 0.22) : alpha(brand[200], 0.8),
  },
  baseShadow:
    mode === 'dark'
      ? 'rgba(10, 6, 24, 0.72) 0px 18px 50px 0px, rgba(76, 29, 149, 0.28) 0px 8px 24px -12px'
      : 'rgba(76, 29, 149, 0.08) 0px 18px 50px 0px, rgba(79, 70, 229, 0.08) 0px 8px 24px -12px',
});

export const getDesignTokens = (mode: PaletteMode) => {
  customShadows[1] =
    mode === 'dark'
      ? 'rgba(10, 6, 24, 0.72) 0px 18px 50px 0px, rgba(76, 29, 149, 0.28) 0px 8px 24px -12px'
      : 'rgba(76, 29, 149, 0.08) 0px 18px 50px 0px, rgba(79, 70, 229, 0.08) 0px 8px 24px -12px';

  return {
    palette: getCommonPalette(mode),
    typography: {
      fontFamily: 'Inter, sans-serif',
      h1: {
        fontSize: defaultTheme.typography.pxToRem(48),
        fontWeight: 700,
        lineHeight: 1.05,
        letterSpacing: -1.2,
      },
      h2: {
        fontSize: defaultTheme.typography.pxToRem(36),
        fontWeight: 700,
        lineHeight: 1.15,
      },
      h3: {
        fontSize: defaultTheme.typography.pxToRem(30),
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h4: {
        fontSize: defaultTheme.typography.pxToRem(24),
        fontWeight: 700,
        lineHeight: 1.35,
      },
      h5: {
        fontSize: defaultTheme.typography.pxToRem(20),
        fontWeight: 600,
      },
      h6: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 600,
      },
      subtitle1: {
        fontSize: defaultTheme.typography.pxToRem(18),
      },
      subtitle2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 600,
      },
      body1: {
        fontSize: defaultTheme.typography.pxToRem(15),
      },
      body2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 400,
      },
      caption: {
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: customShadows,
  };
};

export const colorSchemes = {
  light: {
    palette: getCommonPalette('light'),
  },
};

export const typography = {
  fontFamily: 'Inter, sans-serif',
  h1: {
    fontSize: defaultTheme.typography.pxToRem(48),
    fontWeight: 700,
    lineHeight: 1.05,
    letterSpacing: -1.2,
  },
  h2: {
    fontSize: defaultTheme.typography.pxToRem(36),
    fontWeight: 700,
    lineHeight: 1.15,
  },
  h3: {
    fontSize: defaultTheme.typography.pxToRem(30),
    fontWeight: 700,
    lineHeight: 1.2,
  },
  h4: {
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 700,
    lineHeight: 1.35,
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(18),
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 600,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(15),
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 500,
  },
};

export const shape = {
  borderRadius: 12,
};

// @ts-ignore
const defaultShadows: Shadows = ['none', 'var(--template-palette-baseShadow)', ...defaultTheme.shadows.slice(2)];
export const shadows = defaultShadows;
